import { useUser } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { RiCheckboxCircleFill, RiFileCopyLine, RiLoader4Line } from 'react-icons/ri';
import type { AiConnection } from '@/api/ai-connections';
import { Button } from '@/components/primitives/button';
import { Card, CardContent, CardHeader } from '@/components/primitives/card';
import { CodeBlock } from '@/components/primitives/code-block';
import { CopyButton } from '@/components/primitives/copy-button';
import { showErrorToast } from '@/components/primitives/sonner-helpers';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/primitives/tabs';
import { MCP_URL } from '@/config';
import { getAiClients, getSetupPrompt } from './setup-instructions';

const WAIT_FOR_CONNECTION_MS = 5 * 60 * 1000;

type ConnectCardProps = {
  connections: AiConnection[] | undefined;
  onWaitingChange: (isWaiting: boolean) => void;
};

export function ConnectCard({ connections, onWaitingChange }: ConnectCardProps) {
  const { user } = useUser();
  const email = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? '';
  const clients = getAiClients(MCP_URL);
  const [waitingSince, setWaitingSince] = useState<number>();
  const [isCopied, setIsCopied] = useState(false);

  const newConnection = waitingSince
    ? connections?.find((c) => new Date(c.createdAt).getTime() >= waitingSince - 5000)
    : undefined;

  useEffect(() => {
    onWaitingChange(Boolean(waitingSince) && !newConnection);
  }, [waitingSince, newConnection, onWaitingChange]);

  useEffect(() => {
    if (!waitingSince) return;
    const timer = setTimeout(() => setWaitingSince(undefined), WAIT_FOR_CONNECTION_MS);

    return () => clearTimeout(timer);
  }, [waitingSince]);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(getSetupPrompt({ mcpUrl: MCP_URL, email }));
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      setWaitingSince(Date.now());
    } catch {
      showErrorToast('Could not copy to the clipboard. Copy the steps from a tab below instead.');
    }
  };

  return (
    <Card className="w-full overflow-hidden shadow-none">
      <CardHeader>
        Connect Claude or Codex
        <p className="text-foreground-500 mt-1 text-xs font-normal">
          Let your AI assistant read workflows, email templates and activity, and edit email content as you. It can't
          delete, disable or publish anything.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 rounded-b-xl border-t bg-white p-4">
        <div className="flex flex-col gap-3 rounded-lg border border-neutral-100 bg-neutral-50 p-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-label-sm text-text-strong">Fastest way: paste a setup prompt</span>
            <span className="text-paragraph-xs text-text-soft">
              Paste it into Claude or Codex and it will walk you through the rest.
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="primary" mode="gradient" leadingIcon={RiFileCopyLine} onClick={copyPrompt}>
              {isCopied ? 'Copied' : 'Copy setup prompt'}
            </Button>
            <ConnectionStatus isWaiting={Boolean(waitingSince) && !newConnection} newConnection={newConnection} />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-label-xs text-text-sub">Server URL</span>
          <div className="flex items-center gap-1 rounded-lg border border-neutral-100 px-3 py-1.5">
            <code className="text-foreground-600 font-code flex-1 truncate text-xs">{MCP_URL}</code>
            <CopyButton valueToCopy={MCP_URL} size="xs" />
          </div>
        </div>

        <Tabs defaultValue={clients[0].id}>
          <TabsList variant="regular">
            {clients.map((client) => (
              <TabsTrigger key={client.id} variant="regular" size="md" value={client.id}>
                {client.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {clients.map((client) => (
            <TabsContent key={client.id} value={client.id} className="flex flex-col gap-3 pt-3">
              <ol className="text-paragraph-xs text-text-sub list-decimal space-y-1 pl-4">
                {client.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              {client.command && <CodeBlock code={client.command} language="shell" theme="light" />}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}

function ConnectionStatus({ isWaiting, newConnection }: { isWaiting: boolean; newConnection?: AiConnection }) {
  if (newConnection) {
    return (
      <span className="text-paragraph-xs text-success-base flex items-center gap-1">
        <RiCheckboxCircleFill className="size-4" />
        {newConnection.clientName} connected just now.
      </span>
    );
  }

  if (isWaiting) {
    return (
      <span className="text-paragraph-xs text-text-soft flex items-center gap-1">
        <RiLoader4Line className="size-4 animate-spin" />
        Waiting for the new connection…
      </span>
    );
  }

  return null;
}
